/**
 * Klientele Widget — entry point.
 *
 * Loaded as an IIFE bundle from <script src="…/widget/v1/klientele.js">.
 * Reads its configuration from the host script tag's data-* attributes
 * and immediately instantiates a single widget on the page.
 *
 * Exposes a runtime API at window.KlienteleWidget for SPAs:
 *   KlienteleWidget.hide()
 *   KlienteleWidget.show()
 *   KlienteleWidget.setVisible(boolean)
 *   KlienteleWidget.open()
 *   KlienteleWidget.close()
 */

import { findHostScript, readConfig, parseServerConfig } from './config';
import { registerFonts } from './fonts';
import { KlienteleWidget } from './widget';

declare global {
  interface Window {
    KlienteleWidget?: {
      hide: () => void;
      show: () => void;
      setVisible: (v: boolean) => void;
      open: () => void;
      close: () => void;
    };
  }
}

(async function bootstrap() {
  // Diagnostic — logs the bootstrap decisions so the operator can see
  // WHY the widget did or didn't mount. Removed once the current
  // "mounted despite data-enabled=false" bug is nailed down.
  const log = (...args: unknown[]) => console.log('[Klientele bootstrap]', ...args);

  if (window.KlienteleWidget) {
    log('exit: window.KlienteleWidget already exists');
    return;
  }

  const script = findHostScript();
  if (!script) {
    console.warn('[Klientele] Could not locate host <script> tag — widget not initialized.');
    return;
  }
  log('found script', {
    id: script.id,
    dataEnabled: script.getAttribute('data-enabled'),
    src: script.src,
  });

  const result = readConfig(script);
  if ('error' in result) {
    console.warn('[Klientele] ' + result.error);
    return;
  }
  log('readConfig result', { enabled: result.enabled, apiKey: result.apiKey?.slice(0, 8), clientId: result.clientId });

  // Client-side master kill — before the pre-flight fetch, before any DOM.
  // A disabled widget makes zero network calls and creates zero DOM.
  if (!result.enabled) {
    log('exit: enabled=false');
    return;
  }
  log('continuing past enabled check');

  if (result.hidePatterns.length > 0 && result.showPatterns.length > 0) {
    console.warn('[Klientele] data-hide-on and data-show-on are both set — hide takes precedence.');
  }

  // Pre-flight: confirm the workspace's plan still includes the widget feature
  // AND the origin is allowlisted for the key. If anything fails (bad key,
  // wrong origin, plan doesn't include embed_widget), exit silently so the
  // customer's page never shows a broken or unauthorized widget.
  try {
    const configRes = await fetch(`${result.apiUrl}/api/public/v2/widget/config?clientId=${encodeURIComponent(result.clientId)}`, {
      method: 'GET',
      headers: { 'X-API-Key': result.apiKey },
    });
    if (!configRes.ok) {
      // 401 (bad key), 403 (wrong origin or plan), 404 — all silent in prod.
      // Surface only in dev consoles so embedders can diagnose paste mistakes.
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        console.warn(`[Klientele] Widget disabled (${configRes.status}). Check API key, origin allowlist, and workspace plan.`);
      }
      return;
    }
    // FR-2026-0105: the firm's name, logo and accent, the template's nouns,
    // the urgency chips mapped onto its severities and the voice-note rules.
    const body = (await configRes.json().catch(() => null)) as { data?: unknown } | null;
    const server = parseServerConfig(body?.data);
    if (!server) {
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        console.warn('[Klientele] Widget disabled: the config response was not understood.');
      }
      return;
    }
    result.server = server;
  } catch (err) {
    // Network failure / CORS hard-block — exit silently.
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      console.warn('[Klientele] Widget pre-flight failed:', err);
    }
    return;
  }

  registerFonts();
  const widget = new KlienteleWidget(result);
  widget.mount();

  window.KlienteleWidget = {
    hide: () => widget.hide(),
    show: () => widget.show(),
    setVisible: (v: boolean) => widget.setVisible(v),
    open: () => widget.open(),
    close: () => widget.close(),
  };
})();
